import { useState } from 'react';
import { useSeoMeta } from '@unhead/react';
import { format as formatDate } from 'date-fns';
import { Plus, Shield, Lock } from 'lucide-react';

import { LoginArea } from '@/components/auth/LoginArea';
import { Button } from '@/components/ui/button';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useTodos } from '@/hooks/useTodos';
import { TodayTodos } from '@/components/TodayTodos';
import { CalendarView } from '@/components/CalendarView';
import { TodoForm } from '@/components/TodoForm';
import { TodoItemRow } from '@/components/TodoItemRow';
import type { TodoItem } from '@/lib/types';

const Index = () => {
  useSeoMeta({
    title: 'CrypToDo — Encrypted To-Do List',
    description: 'A privacy-first to-do list powered by Nostr. Your tasks are NIP-44 encrypted — only you can read them.',
  });

  const { user } = useCurrentUser();
  const { data: todos = [], isLoading } = useTodos();
  const [formOpen, setFormOpen] = useState(false);
  const [editingTodo, setEditingTodo] = useState<TodoItem | undefined>(undefined);

  function handleEdit(todo: TodoItem) {
    setEditingTodo(todo);
    setFormOpen(true);
  }

  function handleFormClose(open: boolean) {
    setFormOpen(open);
    if (!open) setEditingTodo(undefined);
  }

  if (!user) {
    return <LandingScreen />;
  }

  const nip44Supported = !!user.signer.nip44;

  if (!nip44Supported) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md text-center space-y-4">
          <Shield className="size-12 mx-auto text-destructive" />
          <h2 className="text-xl font-bold">Signer Upgrade Required</h2>
          <p className="text-muted-foreground text-sm">
            Your current signer does not support NIP-44 encryption. Please upgrade your browser extension or signer app to use CrypToDo.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-border/40 bg-background/80 backdrop-blur-md">
        <div className="container flex h-14 items-center justify-between">
          <div className="flex items-center gap-2">
            <Lock className="size-5 text-primary" />
            <h1 className="text-lg font-bold tracking-tight">CrypToDo</h1>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => {
                setEditingTodo(undefined);
                setFormOpen(true);
              }}
              size="sm"
              className="gap-1.5"
            >
              <Plus className="size-4" />
              <span className="hidden sm:inline">New Todo</span>
            </Button>
            <LoginArea className="max-w-52" />
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="container py-6">
        <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
          {/* Left: Today's list + calendar view */}
          <div className="flex flex-col gap-6">
            <TodayTodos todos={todos} isLoading={isLoading} onEdit={handleEdit} />
            <div className="lg:hidden">
              <CalendarView todos={todos} isLoading={isLoading} onEdit={handleEdit} />
            </div>
            <AllTodos todos={todos} isLoading={isLoading} onEdit={handleEdit} />
          </div>

          {/* Right: Calendar sidebar (desktop) */}
          <aside className="hidden lg:block">
            <div className="sticky top-20">
              <CalendarView todos={todos} isLoading={isLoading} onEdit={handleEdit} />
            </div>
          </aside>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border/40 py-6 mt-8">
        <div className="container flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-muted-foreground">
          <span>
            Vibed with{' '}
            <a
              href="https://shakespeare.diy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              Shakespeare
            </a>
          </span>
          <span className="flex items-center gap-1">
            <Lock className="size-3" />
            End-to-end encrypted with NIP-44
          </span>
        </div>
      </footer>

      {/* Todo Form Dialog */}
      <TodoForm
        open={formOpen}
        onOpenChange={handleFormClose}
        editTodo={editingTodo}
        key={editingTodo?.id ?? 'new'}
      />
    </div>
  );
};

/** All todos section (grouped by date). */
function AllTodos({
  todos,
  isLoading,
  onEdit,
}: {
  todos: TodoItem[];
  isLoading: boolean;
  onEdit: (todo: TodoItem) => void;
}) {
  const grouped = new Map<string, TodoItem[]>();
  for (const todo of todos) {
    const list = grouped.get(todo.date) ?? [];
    list.push(todo);
    grouped.set(todo.date, list);
  }

  const sortedDates = [...grouped.keys()].sort((a, b) => a.localeCompare(b));

  if (isLoading || todos.length === 0) return null;

  return (
    <div className="flex flex-col gap-4">
      <h2 className="text-lg font-bold tracking-tight">All Todos</h2>
      {sortedDates.map((date) => {
        const items = grouped.get(date)!;
        return (
          <div key={date} className="flex flex-col gap-2">
            <h3 className="text-sm font-medium text-muted-foreground">
              {formatDate(new Date(date + 'T00:00:00'), 'EEEE, MMM do')}
            </h3>
            {items.map((todo) => (
              <TodoItemRow key={todo.id} todo={todo} onEdit={onEdit} compact />
            ))}
          </div>
        );
      })}
    </div>
  );
}

/** Landing screen for logged-out users. */
function LandingScreen() {
  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="border-b border-border/40 bg-background/80 backdrop-blur-md">
        <div className="container flex h-14 items-center justify-between">
          <div className="flex items-center gap-2">
            <Lock className="size-5 text-primary" />
            <h1 className="text-lg font-bold tracking-tight">CrypToDo</h1>
          </div>
          <LoginArea className="max-w-52" />
        </div>
      </header>

      {/* Hero */}
      <main className="flex-1 flex items-center justify-center px-4">
        <div className="relative isolate max-w-lg text-center space-y-8">
          {/* Glow */}
          <div className="absolute inset-0 -z-10 blur-3xl opacity-30">
            <div className="mx-auto size-64 rounded-full bg-primary" />
          </div>

          <div className="flex justify-center">
            <div className="flex size-20 items-center justify-center rounded-2xl bg-primary/15 text-primary border border-primary/20 shadow-lg shadow-primary/10">
              <Lock className="size-10" />
            </div>
          </div>

          <div className="space-y-4">
            <h2 className="text-4xl sm:text-5xl font-bold tracking-tight">
              Your tasks,
              <br />
              <span className="text-primary">your secret.</span>
            </h2>
            <p className="text-muted-foreground text-lg leading-relaxed max-w-sm mx-auto">
              A private, encrypted to-do list stored on Nostr. Only you can read your tasks — powered by NIP-44 encryption.
            </p>
          </div>

          <div className="flex flex-col items-center gap-4">
            <LoginArea className="max-w-64" />
            <p className="text-xs text-muted-foreground">
              Sign in with your Nostr identity to get started.
            </p>
          </div>

          <div className="grid grid-cols-3 gap-4 pt-4 text-center">
            {[
              { icon: '🔐', label: 'NIP-44 Encrypted' },
              { icon: '📅', label: 'Calendar Events' },
              { icon: '✏️', label: 'Fully Editable' },
            ].map((feature) => (
              <div key={feature.label} className="space-y-1">
                <span className="text-2xl">{feature.icon}</span>
                <p className="text-xs text-muted-foreground">{feature.label}</p>
              </div>
            ))}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border/40 py-6">
        <div className="container text-center text-xs text-muted-foreground">
          Vibed with{' '}
          <a
            href="https://shakespeare.diy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            Shakespeare
          </a>
        </div>
      </footer>
    </div>
  );
}

export default Index;
